/**
 * Select a sentence, ask about it.
 *
 * The friction this removes: you are mid-derivation, one step does not land,
 * and asking about it means scrolling to the composer and retyping *"the part
 * where you said the boundary term vanishes"*. Most of the time the reader
 * simply doesn't bother, and the confusion stays.
 *
 * Selecting the passage and pressing a button quotes it exactly, so the tutor
 * knows precisely which sentence is meant.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { HelpCircle, Highlighter, Lightbulb, Repeat2 } from "lucide-react";
import { equationSource } from "../markdown/copy.ts";
import { Button } from "./ui/button.tsx";

/** Below this the selection is a stray click, not a passage. */
const MIN_SELECTION = 3;
/** Quoting a whole section back is noise; the tutor has the answer already. */
const MAX_QUOTE = 600;

interface Intent {
  readonly id: string;
  readonly label: string;
  readonly icon: typeof HelpCircle;
  /** Built around the quoted passage. */
  readonly ask: (quote: string) => string;
}

const INTENTS: readonly Intent[] = [
  {
    id: "explain",
    label: "Explain this",
    icon: HelpCircle,
    ask: (quote) =>
      `In your last answer you wrote:\n\n> ${quote}\n\nI don't follow this part. Explain it more slowly, from first principles, and don't assume the step before it was obvious to me.`,
  },
  {
    id: "why",
    label: "Why this step?",
    icon: Repeat2,
    ask: (quote) =>
      `In your last answer you wrote:\n\n> ${quote}\n\nWhy is this step justified? What would go wrong if I did something else here, and how would I have known to do this myself?`,
  },
  {
    id: "example",
    label: "Give an example",
    icon: Lightbulb,
    ask: (quote) =>
      `In your last answer you wrote:\n\n> ${quote}\n\nWork a concrete example of this with real numbers, so I can see it doing something.`,
  },
];

interface SelectionAskProps {
  /** The answer this watches. Selections elsewhere are ignored. */
  readonly containerRef: React.RefObject<HTMLElement | null>;
  readonly onAsk: (prompt: string) => void;
  /** Absent when the answer cannot be annotated (still streaming, or no id). */
  readonly onHighlight?: (quote: string) => void;
  readonly disabled?: boolean;
}

interface Anchor {
  readonly top: number;
  readonly left: number;
  readonly quote: string;
}

export function SelectionAsk({
  containerRef,
  onAsk,
  onHighlight,
  disabled,
}: SelectionAskProps) {
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const dismiss = useCallback(() => setAnchor(null), []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || disabled) return;

    const onSelect = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
        setAnchor(null);
        return;
      }

      const range = selection.getRangeAt(0);
      // Only selections that started inside *this* answer.
      if (!container.contains(range.commonAncestorContainer)) {
        setAnchor(null);
        return;
      }

      const quote = readQuote(range);
      if (quote.length < MIN_SELECTION) {
        setAnchor(null);
        return;
      }

      const rect = range.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;

      setAnchor({
        // Fixed positioning: the answer lives in a scrolling region, and an
        // absolutely positioned popover would drift away from the text.
        top: rect.top,
        left: rect.left + rect.width / 2,
        quote,
      });
    };

    // `selectionchange` fires per character during a drag; settling on
    // pointerup/keyup means the popover appears once, where the drag ended.
    document.addEventListener("pointerup", onSelect);
    document.addEventListener("keyup", onSelect);

    return () => {
      document.removeEventListener("pointerup", onSelect);
      document.removeEventListener("keyup", onSelect);
    };
  }, [containerRef, disabled]);

  // Dismiss on anything that would move the passage out from under it.
  useEffect(() => {
    if (!anchor) return;

    const onDown = (event: MouseEvent) => {
      if (!popoverRef.current?.contains(event.target as Node)) dismiss();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") dismiss();
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
    };
  }, [anchor, dismiss]);

  if (!anchor) return null;

  const ask = (intent: Intent) => {
    onAsk(intent.ask(anchor.quote));
    window.getSelection()?.removeAllRanges();
    dismiss();
  };

  return (
    <div
      ref={popoverRef}
      role="toolbar"
      aria-label="Ask about the selected passage"
      style={{
        position: "fixed",
        top: anchor.top,
        left: anchor.left,
        // Sits above the selection, centred on it.
        transform: "translate(-50%, calc(-100% - 8px))",
      }}
      className="no-print z-50 flex items-center gap-0.5 rounded-lg border bg-popover p-1 font-sans shadow-md"
    >
      {onHighlight && (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onHighlight(anchor.quote);
              window.getSelection()?.removeAllRanges();
              dismiss();
            }}
            className="h-7 gap-1.5 px-2 text-xs text-secondary-foreground hover:text-foreground"
          >
            <Highlighter className="size-3.5" />
            Highlight
          </Button>
          <span aria-hidden="true" className="mx-0.5 h-4 w-px bg-border" />
        </>
      )}

      {INTENTS.map((intent) => (
        <Button
          key={intent.id}
          variant="ghost"
          size="sm"
          onClick={() => ask(intent)}
          className="h-7 gap-1.5 px-2 text-xs text-secondary-foreground hover:text-foreground"
        >
          <intent.icon className="size-3.5" />
          {intent.label}
        </Button>
      ))}
    </div>
  );
}

/**
 * The selected text, with mathematics restored to its source.
 *
 * A selection spanning an equation yields the *rendered* glyphs — and KaTeX
 * renders each atom separately, so the naive string is mangled ("∇⋅E=ρ/ε0"
 * with stray spacing). Swapping in the TeX annotation makes the quote something
 * the tutor can actually read back.
 */
export function readQuote(range: Range): string {
  const fragment = range.cloneContents();

  for (const katex of [...fragment.querySelectorAll(".katex")]) {
    const source = equationSource(katex);
    katex.replaceWith(document.createTextNode(source ? ` ${source} ` : " "));
  }

  // Equation numbers are furniture, not part of the sentence.
  for (const tag of [...fragment.querySelectorAll(".eq-number")]) tag.remove();

  const text = (fragment.textContent ?? "").replace(/\s+/g, " ").trim();
  return text.length > MAX_QUOTE ? `${text.slice(0, MAX_QUOTE).trimEnd()}…` : text;
}
